import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { IBomPart } from 'bom/types';
import { FormProvider, useNotificationContext } from 'core/states';
import { INote } from 'core/types';

import { AddPartNoteFormFields } from './AddPartNoteFormFields';
import { PartNoteType } from './enums';
import { addPartNoteSchema } from './schema';
import { IPartNote, IPartNoteForm } from './types';

interface IProps {
  isOpen: boolean;
  level?: number;
  onClose: (closeSelection?: boolean) => void;
  onSubmit?: (
    reset?: UseFormReset<IPartNoteForm>
  ) => (data: IPartNoteForm) => Promise<{ success: boolean } | void>;
  upsertPartNoteAction?: (
    data: IPartNote
  ) => Promise<{ success: boolean; partNote?: IPartNote; bomParts?: IBomPart[] }>;
  onSubmitSuccess?: (data: { partNotes?: INote[]; bomParts?: IBomPart[] }) => void;
  availableNoteTypes?: PartNoteType[] | string[];
}

export const AddPartNote = ({
  isOpen = false,
  level = 0,
  onClose,
  onSubmit = null,
  upsertPartNoteAction,
  onSubmitSuccess = null,
  availableNoteTypes = Object.values(PartNoteType),
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const defaultValues = useMemo(
    () => ({
      notes: {
        [PartNoteType.DEFAULT]: {
          enabled: true,
        },
      },
    }),
    []
  );

  const handleOnSubmit = (reset: UseFormReset<IPartNoteForm>) => async (data: IPartNoteForm) => {
    if (onSubmit) {
      const result = await onSubmit(reset)(data);

      if (result && !result?.success) {
        return;
      }

      handleOnClose(reset, true);

      return;
    }

    const mappedNotesData = Object.keys(data.notes).map((type) => ({
      type,
      content: data.notes[type].content,
    })) as IPartNote[];

    let success = false;
    const partNotes: INote[] = [];
    const bomParts: IBomPart[] = [];

    await Promise.all(
      mappedNotesData.map(async (noteData: { type: PartNoteType; content: string }) => {
        const { success: upsertSuccess, ...rest } = await upsertPartNoteAction(noteData);

        if (!upsertSuccess) {
          return;
        }

        success = true;

        if (rest?.partNote) {
          partNotes.push(rest.partNote);
        }

        if (rest?.bomParts) {
          bomParts.push(...rest.bomParts);
        }
      })
    );

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess && onSubmitSuccess({ partNotes, bomParts });

      handleOnClose(reset, true);
    }, 1000);
  };

  const handleOnClose = (reset?: UseFormReset<IPartNoteForm>, closeSelection = false) => {
    reset();
    clearNotification();
    onClose(closeSelection);
  };

  return (
    <FormProvider schema={addPartNoteSchema(availableNoteTypes)} defaultValues={defaultValues}>
      <AddPartNoteFormFields
        isOpen={isOpen}
        level={level}
        onClose={handleOnClose}
        onSubmit={handleOnSubmit}
        types={availableNoteTypes}
      />
    </FormProvider>
  );
};
